'use client';
import React, { useState } from 'react';
import { TrendingUp, Calendar, AlertTriangle, CheckCircle, Clock, ChevronDown, ChevronUp, BarChart2, DollarSign, Save, X, Edit3, Info } from 'lucide-react';

// Official LRA property type → tax rate mapping
const LRA_PROPERTY_TYPES = [
  { key: 'commercial_building', label: 'Commercial Building', rate: 0.015, rateDisplay: '1.5%', category: 'Developed Land' },
  { key: 'industrial_building', label: 'Industrial Building', rate: 0.015, rateDisplay: '1.5%', category: 'Developed Land' },
  { key: 'residential_building', label: 'Residential Building', rate: 0.0025, rateDisplay: '0.25%', category: 'Developed Land' },
  { key: 'farm_urban', label: 'Farm in Urban Areas', rate: 0.0033, rateDisplay: '0.33%', category: 'Developed Land' },
  { key: 'farm_outside_urban', label: 'Farm outside Urban Areas', rate: 0.0025, rateDisplay: '0.25%', category: 'Developed Land' },
  { key: 'public_land_commercial', label: 'Building on Public Land – Commercial', rate: 0.01, rateDisplay: '1%', category: 'Developed (Public Land)' },
  { key: 'public_land_residential', label: 'Building on Public Land – Residential', rate: 0.0014, rateDisplay: '0.14%', category: 'Developed (Public Land)' },
  { key: 'city_town_lots', label: 'City/Town Lots (undeveloped)', rate: 0.035, rateDisplay: '3.5%', category: 'Undeveloped – Within City/Town' },
  { key: 'farmland_undeveloped', label: 'Farmland (undeveloped, within city/town)', rate: 0.04, rateDisplay: '4%', category: 'Undeveloped – Within City/Town' },
  { key: 'vacant_acre_plus', label: 'Acre or Above – Vacant Land (within city/town)', rate: 0.045, rateDisplay: '4.5%', category: 'Undeveloped – Within City/Town' },
  { key: 'outside_city_all_land', label: 'All Land without Building (outside city/town)', rate: null, rateDisplay: 'LRD $5.00/acre', category: 'Undeveloped – Outside City/Town' },
];

interface ValuationRecord {
  parcelId: string;
  address: string;
  county: string;
  owner: string;
  currentValue: number;
  marketValue: number;
  lastAssessed: string;
  nextAssessment: string;
  propertyTypeKey: string;
  assessmentHistory: { year: number; value: number; method: string }[];
  appealStatus: 'none' | 'pending' | 'approved' | 'rejected';
  appealReason?: string;
  valuationMethod: 'self-assessment' | 'certified-assessor' | 'professional-firm' | 'farmland';
}

const VALUATIONS: ValuationRecord[] = [
  {
    parcelId: 'LR-MON-0042', address: '14 Broad St, Monrovia', county: 'Montserrado', owner: 'Kollie Trading Co.',
    currentValue: 185000, marketValue: 210000, lastAssessed: '2024-01-15', nextAssessment: '2025-01-15',
    propertyTypeKey: 'commercial_building',
    assessmentHistory: [
      { year: 2024, value: 185000, method: 'Certified Assessor (LRA Approved)' },
      { year: 2023, value: 172000, method: 'Field Assessment' },
      { year: 2022, value: 158000, method: 'Self-Assessment (Method 2)' },
      { year: 2021, value: 145000, method: 'Manual Assessment' },
    ],
    appealStatus: 'none',
    valuationMethod: 'certified-assessor',
  },
  {
    parcelId: 'LR-MON-0117', address: '88 Tubman Blvd, Monrovia', county: 'Montserrado', owner: 'Mary Pewee',
    currentValue: 92000, marketValue: 105000, lastAssessed: '2024-02-20', nextAssessment: '2025-02-20',
    propertyTypeKey: 'residential_building',
    assessmentHistory: [
      { year: 2024, value: 92000, method: 'Self-Assessment (Method 2)' },
      { year: 2023, value: 85000, method: 'Field Assessment' },
      { year: 2022, value: 78000, method: 'Self-Assessment (Method 2)' },
    ],
    appealStatus: 'pending', appealReason: 'Owner disputes market rate; claims comparable properties valued lower',
    valuationMethod: 'self-assessment',
  },
  {
    parcelId: 'LR-GBS-0055', address: 'Buchanan Port Zone', county: 'Grand Bassa', owner: 'LPA Holdings',
    currentValue: 520000, marketValue: 580000, lastAssessed: '2024-03-10', nextAssessment: '2025-03-10',
    propertyTypeKey: 'industrial_building',
    assessmentHistory: [
      { year: 2024, value: 520000, method: 'Professional Firm Valuation (>2,500 sq ft)' },
      { year: 2023, value: 490000, method: 'Professional Firm Valuation' },
      { year: 2022, value: 455000, method: 'Field Assessment' },
      { year: 2021, value: 420000, method: 'Manual Assessment' },
    ],
    appealStatus: 'approved', appealReason: 'Approved reduction due to infrastructure damage',
    valuationMethod: 'professional-firm',
  },
  {
    parcelId: 'LR-BON-0031', address: 'Gbarnga Central Plot 3', county: 'Bong', owner: 'Bong County Dev. Corp.',
    currentValue: 310000, marketValue: 295000, lastAssessed: '2024-01-30', nextAssessment: '2025-01-30',
    propertyTypeKey: 'farm_urban',
    assessmentHistory: [
      { year: 2024, value: 310000, method: 'Self-Assessment (Method 2)' },
      { year: 2023, value: 288000, method: 'Field Assessment' },
      { year: 2022, value: 265000, method: 'Self-Assessment (Method 2)' },
    ],
    appealStatus: 'rejected', appealReason: 'Appeal rejected — valuation consistent with market data',
    valuationMethod: 'self-assessment',
  },
  {
    parcelId: 'LR-GGH-0011', address: 'Zwedru Central Market', county: 'Grand Gedeh', owner: 'Gedeh Commerce Ltd.',
    currentValue: 145000, marketValue: 162000, lastAssessed: '2024-04-05', nextAssessment: '2025-04-05',
    propertyTypeKey: 'commercial_building',
    assessmentHistory: [
      { year: 2024, value: 145000, method: 'Certified Assessor (LRA Approved)' },
      { year: 2023, value: 132000, method: 'Field Assessment' },
    ],
    appealStatus: 'none',
    valuationMethod: 'certified-assessor',
  },
];

const appealConfig = {
  none: { color: '#94a3b8', bg: 'rgba(148,163,184,0.1)', label: 'No Appeal', icon: CheckCircle },
  pending: { color: '#fbbf24', bg: 'rgba(251,191,36,0.1)', label: 'Appeal Pending', icon: Clock },
  approved: { color: '#34d399', bg: 'rgba(52,211,153,0.1)', label: 'Appeal Approved', icon: CheckCircle },
  rejected: { color: '#f87171', bg: 'rgba(248,113,113,0.1)', label: 'Appeal Rejected', icon: AlertTriangle },
};

const valuationMethodLabels: Record<string, { label: string; color: string }> = {
  'self-assessment': { label: 'Self-Assessment (LRA Method 2)', color: '#60a5fa' },
  'certified-assessor': { label: 'Certified Assessor (LRA Approved)', color: '#34d399' },
  'professional-firm': { label: 'Professional Firm (>2,500 sq ft)', color: '#a78bfa' },
  'farmland': { label: 'Farmland Valuation (Method 3)', color: '#fbbf24' },
};

interface SavedPropertyValue {
  value: number;
  note: string;
  setAt: string;
}

export default function PropertyValuationPanel() {
  const [expandedId, setExpandedId] = useState<string | null>('LR-MON-0042');
  const [filterAppeal, setFilterAppeal] = useState<string>('all');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState<string>('');
  const [inputNote, setInputNote] = useState<string>('');
  const [savedValues, setSavedValues] = useState<Record<string, SavedPropertyValue>>({});
  const [savedFlash, setSavedFlash] = useState<string | null>(null);

  const filtered = VALUATIONS.filter((v) => filterAppeal === 'all' || v.appealStatus === filterAppeal);

  const formatCurrency = (val: number) => `$${val.toLocaleString()}`;

  const getLRAType = (key: string) => LRA_PROPERTY_TYPES.find((t) => t.key === key);

  const calcLRATax = (value: number, typeKey: string) => {
    const t = getLRAType(typeKey);
    if (!t || t.rate === null) return null;
    return value * t.rate;
  };

  const handleOpenSetValue = (parcelId: string, currentValue: number) => {
    const existing = savedValues[parcelId];
    setInputValue(existing ? String(existing.value) : String(currentValue));
    setInputNote(existing ? existing.note : '');
    setEditingId(parcelId);
  };

  const handleSaveValue = (parcelId: string) => {
    const parsed = parseFloat(inputValue.replace(/,/g, ''));
    if (isNaN(parsed) || parsed <= 0) return;
    const now = new Date();
    const dateStr = now.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    setSavedValues((prev) => ({
      ...prev,
      [parcelId]: { value: parsed, note: inputNote.trim(), setAt: dateStr },
    }));
    setEditingId(null);
    setSavedFlash(parcelId);
    setTimeout(() => setSavedFlash(null), 2500);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setInputValue('');
    setInputNote('');
  };

  return (
    <div className="space-y-4">
      {/* LRA Valuation Methods Info */}
      <div className="rounded-xl p-3" style={{ backgroundColor: 'rgba(212,160,23,0.06)', border: '1px solid rgba(212,160,23,0.2)' }}>
        <p className="text-xs font-semibold mb-1.5 flex items-center gap-1.5" style={{ color: 'var(--accent)' }}>
          <Info size={13} />
          LRA Self-Assessment Valuation Methods
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs" style={{ color: 'var(--dark-muted)' }}>
          <div className="rounded-lg p-2" style={{ backgroundColor: 'var(--dark-elevated)' }}>
            <p className="font-semibold mb-0.5" style={{ color: '#60a5fa' }}>Method 1 — Certified Assessor</p>
            <p>Use LRA-approved assessor's value. High-end commercial (&gt;2,500 sq ft) requires professional firm with notarized appraisal (valid 5 years).</p>
          </div>
          <div className="rounded-lg p-2" style={{ backgroundColor: 'var(--dark-elevated)' }}>
            <p className="font-semibold mb-0.5" style={{ color: '#34d399' }}>Method 2 — Self-Declaration</p>
            <p>Use purchase price or fair market value if higher. Separate land from building value. Subject to LRA revision if understatement detected.</p>
          </div>
          <div className="rounded-lg p-2" style={{ backgroundColor: 'var(--dark-elevated)' }}>
            <p className="font-semibold mb-0.5" style={{ color: '#fbbf24' }}>Method 3 — Farmland</p>
            <p>Cash crops (rubber, palm, coco, coffee): land value + value per tree. Other farmland: fair market value from deed/purchase document.</p>
          </div>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: 'Total Assessed Value', value: '$1.25B', color: 'var(--accent)', sub: 'All registered parcels' },
          { label: 'Market Value Gap', value: '+8.2%', color: '#34d399', sub: 'Above assessed avg.' },
          { label: 'Pending Appeals', value: '23', color: '#fbbf24', sub: 'Awaiting review' },
          { label: 'Due for Reassessment', value: '189', color: '#f87171', sub: 'Overdue by 90+ days' },
        ].map((s) => (
          <div key={s.label} className="rounded-xl p-4" style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}>
            <p className="text-xs mb-1" style={{ color: 'var(--dark-muted)' }}>{s.label}</p>
            <p className="text-xl font-bold font-mono-data" style={{ color: s.color }}>{s.value}</p>
            <p className="text-xs mt-0.5" style={{ color: 'var(--dark-muted)' }}>{s.sub}</p>
          </div>
        ))}
      </div>

      {/* Filter */}
      <div className="flex items-center gap-2">
        <span className="text-xs font-medium" style={{ color: 'var(--dark-muted)' }}>Filter by appeal:</span>
        {['all', 'none', 'pending', 'approved', 'rejected'].map((f) => (
          <button
            key={f}
            onClick={() => setFilterAppeal(f)}
            className="px-3 py-1 rounded-full text-xs font-medium transition-all capitalize"
            style={{
              backgroundColor: filterAppeal === f ? 'rgba(212,160,23,0.2)' : 'var(--dark-elevated)',
              color: filterAppeal === f ? 'var(--accent)' : 'var(--dark-muted)',
              border: `1px solid ${filterAppeal === f ? 'rgba(212,160,23,0.4)' : 'var(--dark-border)'}`,
            }}
          >
            {f === 'all' ? 'All' : f}
          </button>
        ))}
      </div>

      {/* Valuation records */}
      <div className="space-y-3">
        {filtered.map((v) => {
          const isExpanded = expandedId === v.parcelId;
          const appealCfg = appealConfig[v.appealStatus];
          const AppealIcon = appealCfg.icon;
          const gap = ((v.marketValue - v.currentValue) / v.currentValue * 100).toFixed(1);
          const isAbove = v.marketValue > v.currentValue;
          const saved = savedValues[v.parcelId];
          const isEditing = editingId === v.parcelId;
          const justSaved = savedFlash === v.parcelId;
          const lraType = getLRAType(v.propertyTypeKey);
          const activeValue = saved ? saved.value : v.currentValue;
          const lraTax = lraType && lraType.rate !== null ? calcLRATax(activeValue, v.propertyTypeKey) : null;
          const vmCfg = valuationMethodLabels[v.valuationMethod];

          return (
            <div key={v.parcelId} className="rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}>
              <button
                className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-white/2 transition-colors"
                onClick={() => setExpandedId(isExpanded ? null : v.parcelId)}
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: 'rgba(212,160,23,0.15)' }}>
                    <BarChart2 size={16} style={{ color: 'var(--accent)' }} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-bold font-mono-data" style={{ color: 'var(--accent)' }}>{v.parcelId}</span>
                      <span className="text-xs px-2 py-0.5 rounded-full flex items-center gap-1"
                        style={{ backgroundColor: appealCfg.bg, color: appealCfg.color }}>
                        <AppealIcon size={10} />
                        {appealCfg.label}
                      </span>
                      {lraType && (
                        <span className="text-xs px-2 py-0.5 rounded-full"
                          style={{ backgroundColor: 'rgba(96,165,250,0.1)', color: '#60a5fa', border: '1px solid rgba(96,165,250,0.2)' }}>
                          {lraType.rateDisplay}
                        </span>
                      )}
                      {saved && (
                        <span className="text-xs px-2 py-0.5 rounded-full flex items-center gap-1"
                          style={{ backgroundColor: 'rgba(52,211,153,0.12)', color: '#34d399', border: '1px solid rgba(52,211,153,0.25)' }}>
                          <DollarSign size={9} />
                          Value Set
                        </span>
                      )}
                    </div>
                    <p className="text-xs mt-0.5" style={{ color: 'var(--dark-muted)' }}>
                      {v.address} · {v.owner}
                      {lraType && <span className="ml-1" style={{ color: 'var(--dark-muted)' }}>· {lraType.label}</span>}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right hidden sm:block">
                    <p className="text-sm font-bold font-mono-data" style={{ color: 'var(--dark-text)' }}>
                      {formatCurrency(activeValue)}
                    </p>
                    <p className="text-xs" style={{ color: isAbove ? '#34d399' : '#f87171' }}>
                      Market: {formatCurrency(v.marketValue)} ({isAbove ? '+' : ''}{gap}%)
                    </p>
                    {lraTax !== null && (
                      <p className="text-xs mt-0.5" style={{ color: 'var(--accent)' }}>
                        LRA Tax: {formatCurrency(lraTax)}/yr
                      </p>
                    )}
                  </div>
                  {isExpanded ? <ChevronUp size={16} style={{ color: 'var(--dark-muted)' }} /> : <ChevronDown size={16} style={{ color: 'var(--dark-muted)' }} />}
                </div>
              </button>

              {isExpanded && (
                <div className="px-4 pb-4 border-t" style={{ borderColor: 'var(--dark-border)' }}>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-3">
                    {/* Assessment history */}
                    <div>
                      <p className="text-xs font-semibold mb-2 flex items-center gap-1.5" style={{ color: 'var(--dark-text)' }}>
                        <Calendar size={12} style={{ color: 'var(--accent)' }} />
                        Assessment History
                      </p>
                      <div className="space-y-2">
                        {v.assessmentHistory.map((h, i) => (
                          <div key={h.year} className="flex items-center justify-between p-2 rounded-lg"
                            style={{ backgroundColor: 'var(--dark-elevated)', border: '1px solid var(--dark-border)' }}>
                            <div>
                              <span className="text-xs font-semibold" style={{ color: i === 0 ? 'var(--accent)' : 'var(--dark-text)' }}>
                                {h.year} {i === 0 && <span className="text-xs font-normal ml-1" style={{ color: 'var(--dark-muted)' }}>(Current)</span>}
                              </span>
                              <p className="text-xs mt-0.5" style={{ color: 'var(--dark-muted)' }}>{h.method}</p>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <TrendingUp size={12} style={{ color: '#34d399' }} />
                              <span className="text-xs font-bold font-mono-data" style={{ color: 'var(--dark-text)' }}>
                                {formatCurrency(h.value)}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Right column: LRA tax calc + schedule + set value */}
                    <div className="space-y-3">
                      {/* LRA Tax Calculation */}
                      {lraType && (
                        <div className="p-3 rounded-lg" style={{ backgroundColor: 'rgba(212,160,23,0.06)', border: '1px solid rgba(212,160,23,0.2)' }}>
                          <p className="text-xs font-semibold mb-2" style={{ color: 'var(--accent)' }}>LRA Tax Calculation</p>
                          <div className="space-y-1 text-xs">
                            <div className="flex justify-between">
                              <span style={{ color: 'var(--dark-muted)' }}>Property Type</span>
                              <span style={{ color: 'var(--dark-text)' }}>{lraType.label}</span>
                            </div>
                            <div className="flex justify-between">
                              <span style={{ color: 'var(--dark-muted)' }}>Category</span>
                              <span style={{ color: 'var(--dark-text)' }}>{lraType.category}</span>
                            </div>
                            <div className="flex justify-between">
                              <span style={{ color: 'var(--dark-muted)' }}>LRA Rate</span>
                              <span className="font-bold" style={{ color: 'var(--accent)' }}>{lraType.rateDisplay}</span>
                            </div>
                            <div className="flex justify-between">
                              <span style={{ color: 'var(--dark-muted)' }}>Declared Value</span>
                              <span style={{ color: 'var(--dark-text)' }}>{formatCurrency(activeValue)}</span>
                            </div>
                            <div className="flex justify-between border-t pt-1 mt-1" style={{ borderColor: 'rgba(212,160,23,0.2)' }}>
                              <span className="font-semibold" style={{ color: 'var(--dark-text)' }}>Annual Tax Due</span>
                              <span className="font-bold" style={{ color: 'var(--accent)' }}>
                                {lraTax !== null ? formatCurrency(lraTax) : lraType.rateDisplay}
                              </span>
                            </div>
                          </div>
                          <p className="text-xs mt-2" style={{ color: 'var(--dark-muted)' }}>
                            Valuation Method: <span style={{ color: vmCfg.color }}>{vmCfg.label}</span>
                          </p>
                        </div>
                      )}

                      {/* Assessment schedule */}
                      <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--dark-elevated)', border: '1px solid var(--dark-border)' }}>
                        <p className="text-xs font-semibold mb-2" style={{ color: 'var(--dark-text)' }}>Assessment Schedule</p>
                        <div className="space-y-1 text-xs">
                          <div className="flex justify-between">
                            <span style={{ color: 'var(--dark-muted)' }}>Last Assessed</span>
                            <span style={{ color: 'var(--dark-text)' }}>{v.lastAssessed}</span>
                          </div>
                          <div className="flex justify-between">
                            <span style={{ color: 'var(--dark-muted)' }}>Next Assessment</span>
                            <span style={{ color: 'var(--dark-text)' }}>{v.nextAssessment}</span>
                          </div>
                          <div className="flex justify-between">
                            <span style={{ color: 'var(--dark-muted)' }}>Tax Due Date</span>
                            <span style={{ color: '#fbbf24' }}>July 1st (annual)</span>
                          </div>
                        </div>
                      </div>

                      {/* Appeal status */}
                      {v.appealStatus !== 'none' && (
                        <div className="p-3 rounded-lg" style={{ backgroundColor: appealConfig[v.appealStatus].bg, border: `1px solid ${appealConfig[v.appealStatus].color}30` }}>
                          <p className="text-xs font-semibold mb-1" style={{ color: appealConfig[v.appealStatus].color }}>
                            {appealConfig[v.appealStatus].label}
                          </p>
                          {v.appealReason && (
                            <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>{v.appealReason}</p>
                          )}
                        </div>
                      )}

                      {/* Set / edit declared value */}
                      {isEditing ? (
                        <div className="p-3 rounded-lg space-y-2" style={{ backgroundColor: 'var(--dark-elevated)', border: '1px solid rgba(212,160,23,0.3)' }}>
                          <p className="text-xs font-semibold" style={{ color: 'var(--accent)' }}>Set Declared Market Value (USD)</p>
                          <input
                            type="text"
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            placeholder="e.g. 185000"
                            className="w-full px-3 py-1.5 rounded-lg text-sm outline-none"
                            style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)', color: 'var(--dark-text)' }}
                          />
                          <input
                            type="text"
                            value={inputNote}
                            onChange={(e) => setInputNote(e.target.value)}
                            placeholder="Note (optional)"
                            className="w-full px-3 py-1.5 rounded-lg text-sm outline-none"
                            style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)', color: 'var(--dark-text)' }}
                          />
                          <div className="flex gap-2">
                            <button onClick={() => handleSaveValue(v.parcelId)}
                              className="flex-1 py-1.5 rounded-lg text-xs font-semibold flex items-center justify-center gap-1"
                              style={{ background: 'linear-gradient(135deg,#b8860b,#d4a017)', color: '#070e1f' }}>
                              <Save size={11} /> Save
                            </button>
                            <button onClick={handleCancelEdit}
                              className="px-3 py-1.5 rounded-lg text-xs"
                              style={{ backgroundColor: 'var(--dark-card)', color: 'var(--dark-muted)', border: '1px solid var(--dark-border)' }}>
                              <X size={11} />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleOpenSetValue(v.parcelId, v.currentValue)}
                          className="w-full py-2 rounded-lg text-xs font-medium flex items-center justify-center gap-1.5 transition-all"
                          style={{
                            backgroundColor: justSaved ? 'rgba(52,211,153,0.12)' : 'rgba(212,160,23,0.08)',
                            color: justSaved ? '#34d399' : 'var(--accent)',
                            border: `1px solid ${justSaved ? 'rgba(52,211,153,0.3)' : 'rgba(212,160,23,0.25)'}`,
                          }}>
                          <Edit3 size={11} />
                          {justSaved ? 'Value Saved ✓' : 'Set Declared Market Value'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* LRA Disclaimer */}
      <div className="rounded-xl p-3 flex items-start gap-2" style={{ backgroundColor: 'rgba(248,113,113,0.05)', border: '1px solid rgba(248,113,113,0.2)' }}>
        <AlertTriangle size={14} style={{ color: '#f87171', flexShrink: 0, marginTop: 1 }} />
        <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>
          <span className="font-semibold" style={{ color: '#f87171' }}>LRA Disclaimer: </span>
          The LRA maintains its rights to revise and impose additional tax on properties valued by the taxpayer applying self-assessment (Method 2) in case of understatement of value. Significant under-declaration is subject to additional assessment bill. Taxpayers are advised to provide truthful declarations. Failure to file within 30 days of acquisition is punishable by 10% additional payment, increased by 5% monthly (Section 51).
        </p>
      </div>
    </div>
  );
}
